import {createStore, createEvent, createEffect, sample} from 'effector';
import {TermsState, FetchTermsRequest, VocabularyType, TermsApiResponse} from './types';
import {termsService} from './service';

// Events
export const fetchTermsRequested = createEvent<FetchTermsRequest>();
export const fetchAllTermsRequested = createEvent<{force?: boolean}>();
export const clearTermsError = createEvent<VocabularyType>();

// Effects
export const fetchTermsFx = createEffect(
  async (request: FetchTermsRequest): Promise<{vocId: VocabularyType; data: TermsApiResponse}> => {
    try {
      const data = await termsService.getTermsByVocId(request.vocId);
      return {vocId: request.vocId, data};
    } catch (error: any) {
      // Add vocId to error for proper state handling
      error.vocId = request.vocId;
      throw error;
    }
  },
);

export const fetchAllTermsFx = createEffect(async (): Promise<{[K in VocabularyType]: TermsApiResponse}> => {
  return await termsService.getAllTerms();
});

// Initialize store with all vocabulary types
const createInitialState = (): TermsState => {
  const vocabularies: VocabularyType[] = [
    'countries',
    'expenditure',
    'expertise',
    'funding_range',
    'funding_type',
    'looking_for',
    'notification_type',
    'organization_categories',
    'organization_employees_number',
    'organization_turnover',
    'plans',
    'programme',
    'sectors',
    'tags',
  ];

  return vocabularies.reduce((acc, vocId) => {
    acc[vocId] = {
      data: [],
      loading: false,
      error: null,
      lastFetched: null,
    };
    return acc;
  }, {} as TermsState);
};

export const $termsStore = createStore<TermsState>(createInitialState());

// Store handlers
$termsStore
  .on(fetchTermsRequested, (state, {vocId}) => ({
    ...state,
    [vocId]: {
      ...state[vocId],
      loading: true,
      error: null,
    },
  }))
  .on(fetchTermsFx.doneData, (state, {vocId, data}) => ({
    ...state,
    [vocId]: {
      ...state[vocId],
      data: data.data,
      loading: false,
      error: null,
      lastFetched: Date.now(),
    },
  }))
  .on(fetchTermsFx.failData, (state, error: any) => {
    // We need to get the vocId from somewhere - let's store it in the error context
    const vocId = error.vocId || 'countries'; // fallback
    return {
      ...state,
      [vocId as VocabularyType]: {
        ...state[vocId as VocabularyType],
        loading: false,
        error: error.message || 'Failed to fetch terms',
      },
    };
  })
  .on(fetchAllTermsRequested, (state) => {
    const newState = {...state};
    Object.keys(newState).forEach((key) => {
      const vocId = key as VocabularyType;
      newState[vocId] = {
        ...newState[vocId],
        loading: true,
        error: null,
      };
    });
    return newState;
  })
  .on(fetchAllTermsFx.doneData, (state, allTermsData) => {
    const newState = {...state};
    Object.entries(allTermsData).forEach(([vocId, data]) => {
      const vocabularyId = vocId as VocabularyType;
      newState[vocabularyId] = {
        ...newState[vocabularyId],
        data: data.data,
        loading: false,
        error: null,
        lastFetched: Date.now(),
      };
    });
    return newState;
  })
  .on(fetchAllTermsFx.failData, (state, error) => {
    const newState = {...state};
    Object.keys(newState).forEach((key) => {
      const vocId = key as VocabularyType;
      newState[vocId] = {
        ...newState[vocId],
        loading: false,
        error: error.message || 'Failed to fetch all terms',
      };
    });
    return newState;
  })
  .on(clearTermsError, (state, vocId) => ({
    ...state,
    [vocId]: {
      ...state[vocId],
      error: null,
    },
  }));

// Samples
sample({
  clock: fetchTermsRequested,
  target: fetchTermsFx,
});

sample({
  clock: fetchAllTermsRequested,
  target: fetchAllTermsFx,
});

// Selectors (derived stores)
export const $countriesTerms = $termsStore.map((state) => state.countries);
export const $expertiseTerms = $termsStore.map((state) => state.expertise);
export const $sectorsTerms = $termsStore.map((state) => state.sectors);
export const $programmeTerms = $termsStore.map((state) => state.programme);
export const $fundingRangeTerms = $termsStore.map((state) => state.funding_range);
export const $fundingTypeTerms = $termsStore.map((state) => state.funding_type);
export const $lookingForTerms = $termsStore.map((state) => state.looking_for);
export const $organizationCategoriesTerms = $termsStore.map((state) => state.organization_categories);
export const $organizationEmployeesNumberTerms = $termsStore.map((state) => state.organization_employees_number);
export const $organizationTurnoverTerms = $termsStore.map((state) => state.organization_turnover);
export const $expenditureTerms = $termsStore.map((state) => state.expenditure);
export const $notificationTypeTerms = $termsStore.map((state) => state.notification_type);
export const $plansTerms = $termsStore.map((state) => state.plans);
export const $tagsTerms = $termsStore.map((state) => state.tags);

// Helper to get terms by vocabulary ID
export const getTermsByVocId = (state: TermsState, vocId: VocabularyType) => state[vocId];

// Helper to check if terms are loading
export const isTermsLoading = (state: TermsState) => Object.values(state).some((termState) => termState.loading);
